import { Component, inject, signal } from '@angular/core';
import { disabled, form, FormField, min, required } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { PlatformBillingBasisObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-basis.object';
import { BaseDialogComponent } from '../../../../../../../components/kit/base-dialog/base-dialog.component';
import { DateInputComponent } from '../../../../../../../components/kit/input/date-input-v2/date-input.component';
import { SelectInputComponent } from '../../../../../../../components/kit/input/select-input-v2/select-input.component';
import { NumberInputComponent } from '../../../../../../../components/kit/input/text-input-v2/number-input.component';
import { successCreated } from '../../../../../content.error';
import { PlatformBillingBasisFormModel, PlatformBillingSelectOption } from '../organization-platform-billing.types';
import { OrganizationPlatformBillingBasisCreateDialogStore } from './organization-platform-billing-basis-create-dialog.store';

@Component({
    selector: 'app-organization-platform-billing-basis-create-dialog',
    templateUrl: './organization-platform-billing-basis-create-dialog.component.html',
    providers: [OrganizationPlatformBillingBasisCreateDialogStore],
    imports: [FormField, SelectInputComponent, DateInputComponent, NumberInputComponent, TranslatePipe],
})
export class OrganizationPlatformBillingBasisCreateDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(OrganizationPlatformBillingBasisCreateDialogStore);
    protected readonly organizationId = this.dialogConfig.data.organizationId as number;
    protected readonly serviceOptions = this.dialogConfig.data.serviceOptions as PlatformBillingSelectOption[];
    protected readonly prefix = 'organization.platformBilling.fields.';
    protected readonly basis = this.dialogConfig.data.basis as PlatformBillingBasisObject | null;

    protected readonly formData = signal<PlatformBillingBasisFormModel>({
        serviceId: this.basis?.serviceId ?? null,
        period: this.basis ? new Date(`${this.basis.periodFrom}T12:00:00`) : new Date(),
        manualNetAmount: null,
    });

    protected readonly formDef = form(this.formData, (schema) => {
        required(schema.serviceId);
        required(schema.period);
        disabled(schema.serviceId, () => !!this.basis);
        disabled(schema.period, () => !!this.basis);
        min(schema.manualNetAmount, 0);
    });

    public override async save(): Promise<void> {
        if (this.formDef().invalid()) {
            this.formDef().markAsTouched();
            return;
        }

        const value = this.formData();
        if (value.serviceId === null || value.period === null) return;
        const periodFrom = this.basis?.periodFrom ?? this.toDateString(new Date(value.period.getFullYear(), value.period.getMonth(), 1));
        const periodTo = this.basis?.periodTo ?? this.toDateString(new Date(value.period.getFullYear(), value.period.getMonth() + 1, 0));
        if (!periodFrom || !periodTo) return;
        await this.store.api.createPlatformBillingBasis({
            organizationId: this.organizationId,
            serviceId: value.serviceId,
            periodFrom,
            periodTo,
            replacesBasisId: this.basis?.id ?? null,
            manualNetAmount: this.basis ? value.manualNetAmount : null,
        });
        successCreated();
        this.dialogRef.close(true);
    }

    private toDateString(value: Date | null): string | null {
        if (!value) return null;
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
